import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";

import {
  Column,
  MultiSelect,
  NumberInput,
  Row,
  Select,
  Text,
} from "@hightouch/htui";
import moment from "moment";

import { MinimalParentModel } from "src/components/audiences/types";
import { HiddenFormLabel } from "src/components/form/hidden-form-label";
import {
  AbsoluteRelativeTimestampOperators,
  CalendarIntervalOptions,
  CalendarIntervalOptionsSingular,
  CalendarIntervalUnit,
  DateIntervalOptions,
  FilterableColumn,
  IntervalOperators,
  IntervalOptions,
  IntervalUnit,
  IntervalValue,
  Operator,
  OperatorsWithoutValue,
  PropertyCondition,
  RelativeDirection,
  TimeType,
  TimestampOperator,
  ValueType,
  Window,
  WithinOperators,
  getMatchingGranularity,
  intervalUnitSupportsCalendarEquivalent,
  isCalendarInterval,
  isColumnTypeValue,
  isPropertyCondition,
} from "src/types/visual";
import { DateTimeSelect } from "src/ui/datetime-select";

import { isPresent } from "ts-extras";
import { ErrorMessage } from "./error-message";
import { PropertySelector } from "./property-selector";
import {
  AbsoluteTimeRangeValue,
  granularitySupportsCurrent,
  isConditionComparingToTrait,
  newDate,
  sortedAbsoluteRangeBounds,
} from "./utils";
import {
  EventCondition,
  isIntervalType,
  isTimestampGranularityTimeType,
  PropertyOptions,
  TimeRangeField,
  TimestampGranularity,
  TimestampGranularityTimeType,
} from "@hightouch/lib/query/visual/types";
import { dayOfWeekOptions } from "./utils";
import { TimeSelect } from "src/ui/time-select";
import { UpdaterEnforceDefined } from "src/utils/immutable-helpers";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import {
  getMaxWindowQuantity,
  isDenied,
} from "src/components/explore/restrictions";

type Props = {
  error?: ReactNode;
  isDisabled?: boolean;
  condition: PropertyCondition | Window;
  onUpdate: (
    updater: UpdaterEnforceDefined<PropertyCondition | Window>,
  ) => void;
  hideTime: boolean;
  showRelativeDirections?: boolean;
  columns?: FilterableColumn[] | undefined;
  parent?: MinimalParentModel | undefined;
  removePortal?: boolean;
  allowPropertyComparison: boolean;
  includeParentInPropertyComparison?: boolean;
  /** Clamp relative interval inputs so they can't reach further back than
   * this many days. Used for event windows on surfaces backed by a bounded
   * event cache (predictive eligibility filters). */
  maxRelativeWindowDays?: number;
};

const isTimestampBetweenOperator = (
  operator: Operator,
): operator is
  | TimestampOperator.Between
  | TimestampOperator.BetweenInclusive => {
  return (
    operator === TimestampOperator.Between ||
    operator === TimestampOperator.BetweenInclusive
  );
};

const operatorSupportsDayOfWeek = (
  operator: TimestampOperator,
): operator is TimestampOperator.Equals => {
  return operator === TimestampOperator.Equals;
};

const timeOfDayOperators = new Set([
  TimestampOperator.Before,
  TimestampOperator.BeforeInclusive,
  TimestampOperator.After,
  TimestampOperator.AfterInclusive,
  TimestampOperator.Between,
  TimestampOperator.BetweenInclusive,
]);

const operatorSupportsTimeOfDay = (operator: TimestampOperator) =>
  timeOfDayOperators.has(operator);

const onlySpreadObj = (val: unknown) =>
  typeof val === "object" && !Array.isArray(val) ? val : {};

const defaultRelativeValue = {
  quantity: 1,
  interval: IntervalUnit.Month,
  direction: RelativeDirection.Backward,
};

export const TimestampInput: FC<Readonly<Props>> = ({
  condition,
  isDisabled = false,
  hideTime,
  error,
  showRelativeDirections = true,
  onUpdate,
  columns,
  parent,
  removePortal,
  allowPropertyComparison,
  includeParentInPropertyComparison,
  maxRelativeWindowDays,
}) => {
  const { restrictions } = useQueryBuilderContext();
  const denyRelativeDates = isDenied(restrictions, "relative-dates");

  // Set the default state to be a relative timestamp (absolute when
  // relative dates are denied)
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!condition?.timeType) {
      if (isColumnTypeValue(condition?.value)) {
        return;
      }

      onUpdate((condition) => {
        if (denyRelativeDates) {
          condition.timeType = TimeType.Absolute;
          return;
        }
        condition.timeType = TimeType.Relative;
        // Between operator has a value of type `TimeRangeValue` consisting of `before` and `after` objects
        // so we set the relative direction where we initialize the default value (in TimeIntervalInput)
        if (isTimestampBetweenOperator(condition?.operator)) {
          condition.value = {
            ...condition.value,
            direction:
              condition?.value?.direction ?? RelativeDirection.Backward,
          };
        }
      });
    }
  }, [
    condition?.timeType,
    condition?.operator,
    condition?.value,
    denyRelativeDates,
  ]);

  if (AbsoluteRelativeTimestampOperators.includes(condition.operator)) {
    return (
      <AbsoluteRelativeTimeInput
        condition={condition}
        isDisabled={isDisabled}
        error={error}
        hideTime={hideTime}
        onUpdate={onUpdate}
        columns={columns}
        parent={parent}
        removePortal={removePortal}
        allowPropertyComparison={allowPropertyComparison}
        includeParentInPropertyComparison={includeParentInPropertyComparison}
        maxRelativeWindowDays={maxRelativeWindowDays}
      />
    );
  }

  return (
    <Row gap={2}>
      {IntervalOperators.includes(condition.operator) ? (
        <TimeIntervalInput
          isDisabled={isDisabled}
          error={error}
          hideTime={hideTime}
          onChange={(value, timestampGranularity) => {
            onUpdate((condition) => {
              condition.value = value;
              if (timestampGranularity) {
                if (!condition.propertyOptions) {
                  condition.propertyOptions = {};
                }
                condition.propertyOptions.timestampGranularity =
                  timestampGranularity;
              }
            });
          }}
          showRelativeDirections={showRelativeDirections}
          value={condition.value}
          timestampGranularity={condition.propertyOptions?.timestampGranularity}
          removePortal={removePortal}
          showCurrentOption={!WithinOperators.includes(condition.operator)}
          maxRelativeWindowDays={maxRelativeWindowDays}
        />
      ) : (
        !OperatorsWithoutValue.includes(condition.operator) && (
          <AbsoluteTimestampInput
            isDisabled={isDisabled}
            hideTime={hideTime}
            onChange={(value) =>
              onUpdate((condition) => {
                condition.value = value;
              })
            }
            value={condition.value}
          />
        )
      )}
    </Row>
  );
};

const DayOfWeekInput = (props: {
  value: string[];
  onChange: (val?: string[]) => void;
}) => {
  const { value, onChange } = props;
  return (
    <MultiSelect
      options={dayOfWeekOptions}
      value={value}
      onChange={onChange}
      width="auto"
    />
  );
};

const TimeOfDayInput = (props: {
  value: string;
  onChange: (val?: string) => void;
}) => {
  const { value, onChange } = props;
  const dateVal = useMemo(() => newDate(value), [value]);

  const handleChange = (newValue: Date) => {
    onChange(formatValue(newValue));
  };
  return <TimeSelect value={dateVal} onChange={handleChange} hideTimeZone />;
};

const getTimestampGranularityTimeType = (
  condition: PropertyCondition | Window,
) => {
  const granularity = condition.propertyOptions?.timestampGranularity;
  if (isTimestampGranularityTimeType(granularity)) {
    return granularity;
  }
  return undefined;
};

type Current = "current";
type TraitComparison = "trait";

const getIsCurrentTimeType = (
  value: any,
  granularity?: TimestampGranularity,
) => {
  return value?.quantity === 0 && granularitySupportsCurrent(granularity);
};

const setTimestampGranularityMutative = (
  condition: PropertyCondition | Window,
  timestampGranularity?: TimestampGranularity,
) => {
  if (!condition.propertyOptions) {
    condition.propertyOptions = {};
  }
  condition.propertyOptions.timestampGranularity = timestampGranularity;
};

const onlyKeepCalendarIntervalUnits = (interval?: IntervalUnit) =>
  intervalUnitSupportsCalendarEquivalent(interval)
    ? interval
    : IntervalUnit.Month;

const AbsoluteRelativeTimeInput = ({
  condition,
  error,
  onUpdate,
  hideTime,
  columns,
  parent,
  removePortal,
  isDisabled = false,
  allowPropertyComparison,
  includeParentInPropertyComparison,
  maxRelativeWindowDays,
}: Props & {
  columns: FilterableColumn[] | undefined;
  parent: MinimalParentModel | undefined;
}) => {
  const { audienceTraitRhsFilters } = useFlags();
  const { restrictions } = useQueryBuilderContext();
  const denyRelativeDates = isDenied(restrictions, "relative-dates");
  const granularity = condition.propertyOptions?.timestampGranularity;
  const isCurrentTimeType = getIsCurrentTimeType(condition.value, granularity);

  // Detect if comparing to traits (only for PropertyCondition, not Window)
  const isTraitSelected =
    isPropertyCondition(condition) && isConditionComparingToTrait(condition);

  const handleTimeComparisonChange = (
    timeComparisonType:
      | TimeType
      | RelativeDirection
      | TimestampGranularityTimeType
      | ValueType.Column
      | Current
      | TraitComparison
      | undefined,
  ) => {
    if (timeComparisonType === undefined) {
      return;
    }
    if (timeComparisonType === "current") {
      const newInterval = onlyKeepCalendarIntervalUnits(
        condition.value?.interval,
      );
      onUpdate((condition) => {
        condition.value = {
          quantity: 0,
          direction: RelativeDirection.Backward,
          interval: newInterval,
        };
        condition.timeType = TimeType.Relative;
        setTimestampGranularityMutative(
          condition,
          getMatchingGranularity(newInterval),
        );
      });
    } else if (
      timeComparisonType === TimeType.Relative ||
      timeComparisonType === TimeType.Absolute
    ) {
      onUpdate((condition) => {
        condition.value = null;
        condition.timeType = timeComparisonType;
        setTimestampGranularityMutative(condition, TimestampGranularity.Full);
      });
    } else if (timeComparisonType === "trait") {
      onUpdate((condition) => {
        condition.value = {
          type: ValueType.Column,
          property: {
            type: "trait",
            traitDefinitionId: "",
            conditions: [],
          },
        };
        condition.timeType = undefined;
        setTimestampGranularityMutative(condition, TimestampGranularity.Full);
      });
    } else if (timeComparisonType === ValueType.Column) {
      onUpdate((condition) => {
        condition.value = { type: ValueType.Column };
        condition.timeType = undefined;
        setTimestampGranularityMutative(condition, TimestampGranularity.Full);
      });
    } else if (isTimestampGranularityTimeType(timeComparisonType)) {
      if (timeComparisonType === TimestampGranularity.DayOfWeek) {
        onUpdate((condition) => {
          condition.value = [dayOfWeekOptions[0]!.value];
          condition.timeType = TimeType.Relative;
          setTimestampGranularityMutative(condition, timeComparisonType);
        });
      } else {
        onUpdate((condition) => {
          condition.value = formatValue(newDate());
          condition.timeType = TimeType.Relative;
          setTimestampGranularityMutative(condition, timeComparisonType);
        });
      }
    } else {
      const newValue = {
        ...onlySpreadObj(condition.value),
        type: undefined,
        direction: timeComparisonType,
      };
      if (isCurrentTimeType) {
        (newValue as unknown as IntervalValue).quantity = 1;
      }

      onUpdate((condition) => {
        condition.value = newValue;
        condition.timeType = TimeType.Relative;
        setTimestampGranularityMutative(condition, TimestampGranularity.Full);
      });
    }
  };

  const isBetweenOperator = isTimestampBetweenOperator(condition.operator);

  // Time-of-day and day-of-week stay available when relative dates are
  // denied since they only inspect components of the column value.
  const timeTypeOptions = (
    isBetweenOperator
      ? [
          denyRelativeDates
            ? undefined
            : { label: "relative", value: TimeType.Relative },
          { label: "absolute", value: TimeType.Absolute },
          hideTime
            ? undefined
            : { label: "time of day", value: TimestampGranularity.Time },
        ]
      : [
          denyRelativeDates
            ? undefined
            : { label: "previous", value: RelativeDirection.Backward },
          denyRelativeDates
            ? undefined
            : { label: "next", value: RelativeDirection.Forward },
          granularitySupportsCurrent(granularity) && !denyRelativeDates
            ? { label: "current", value: "current" }
            : undefined,
          { label: "absolute", value: TimeType.Absolute },
          isPropertyCondition(condition) &&
          allowPropertyComparison &&
          !isTraitSelected
            ? {
                label: "property",
                value: ValueType.Column,
              }
            : undefined,
          isPropertyCondition(condition) &&
          allowPropertyComparison &&
          audienceTraitRhsFilters &&
          !isDenied(restrictions, "trait-comparisons")
            ? {
                label: "trait",
                value: "trait",
              }
            : undefined,
          operatorSupportsDayOfWeek(condition.operator)
            ? { label: "day of week", value: TimestampGranularity.DayOfWeek }
            : undefined,
          operatorSupportsTimeOfDay(condition.operator) && !hideTime
            ? { label: "time of day", value: TimestampGranularity.Time }
            : undefined,
        ]
  ).filter(isPresent);

  return (
    <>
      <Select
        removePortal={removePortal}
        isDisabled={isDisabled}
        placeholder="Select an option"
        options={timeTypeOptions}
        value={
          isTraitSelected
            ? "trait"
            : (((isCurrentTimeType && "current") ||
                condition?.value?.direction) ??
              getTimestampGranularityTimeType(condition) ??
              condition.timeType ??
              condition?.value?.type)
        }
        width="auto"
        onChange={handleTimeComparisonChange}
      />
      {isBetweenOperator ? (
        <TimeRangeInput
          isDisabled={isDisabled}
          value={condition.value}
          propertyOptions={condition.propertyOptions}
          error={error}
          hideTime={hideTime}
          onUpdate={onUpdate}
          timeType={condition.timeType ?? TimeType.Relative}
          removePortal={removePortal}
          maxRelativeWindowDays={maxRelativeWindowDays}
        />
      ) : condition.timeType === TimeType.Absolute ? (
        <AbsoluteTimestampInput
          isDisabled={isDisabled}
          hideTime={hideTime}
          onChange={(value) =>
            onUpdate((condition) => {
              condition.value = value;
            })
          }
          value={condition.value}
        />
      ) : isPropertyCondition(condition) &&
        condition.value?.type === ValueType.Column ? (
        <PropertySelector
          isDisabled={isDisabled}
          condition={condition}
          error={error}
          onChange={(value) =>
            onUpdate((condition) => {
              condition.value = value;
            })
          }
          value={condition.value}
          columns={columns}
          parent={parent}
          removePortal={removePortal}
          includeParentModelProperties={includeParentInPropertyComparison}
          selectingTraits={isTraitSelected}
        />
      ) : granularity === TimestampGranularity.DayOfWeek ? (
        <DayOfWeekInput
          value={condition.value}
          onChange={(value) =>
            onUpdate((condition) => {
              condition.value = value;
            })
          }
        />
      ) : granularity === TimestampGranularity.Time ? (
        <TimeOfDayInput
          value={condition.value}
          onChange={(value) =>
            onUpdate((condition) => {
              condition.value = value;
            })
          }
        />
      ) : (
        <TimeIntervalInput
          isDisabled={isDisabled}
          error={error}
          hideTime={hideTime}
          showOnlyCalendarIntervals={
            condition.operator === TimestampOperator.Equals
          }
          onChange={(value, timestampGranularity) => {
            onUpdate((condition) => {
              condition.value = value;
              if (timestampGranularity) {
                setTimestampGranularityMutative(
                  condition,
                  timestampGranularity,
                );
              }
            });
          }}
          showRelativeDirections={false}
          value={condition.value}
          removePortal={removePortal}
          timestampGranularity={condition.propertyOptions?.timestampGranularity}
          maxRelativeWindowDays={maxRelativeWindowDays}
        />
      )}
    </>
  );
};

interface TimeRangeInputProps {
  error?: ReactNode;
  removePortal?: boolean;
  onUpdate: (
    updater: UpdaterEnforceDefined<PropertyCondition | Window>,
  ) => void;
  propertyOptions?: PropertyOptions;
  value: any;
  hideTime: boolean;
  isDisabled?: boolean;
  maxRelativeWindowDays?: number;
}

interface AbsoluteTimeRangeInputProps extends TimeRangeInputProps {
  timeType: TimeType.Absolute;
}

interface RelativeTimeRangeInputProps extends TimeRangeInputProps {
  timeType: TimeType.Relative;
}

const TimeRangeInput: FC<
  AbsoluteTimeRangeInputProps | RelativeTimeRangeInputProps
> = (props) => {
  // Handle `before` initialization here to avoid infinite loops between `before` and `after` initializing
  const { value } = props;
  const valuesShouldBeStrings =
    props.propertyOptions?.timestampGranularity === TimestampGranularity.Time ||
    props.timeType == TimeType.Absolute;
  const beforeIsInvalid = valuesShouldBeStrings
    ? !value?.before
    : relativeValueNeedsInitialization(value?.before);

  useEffect(() => {
    if (beforeIsInvalid) {
      props.onUpdate((condition) => {
        changeConditionValueToObject(condition);
        condition.value.before = valuesShouldBeStrings
          ? formatValue(newDate())
          : defaultRelativeValue;
      });
    }
  });
  if (beforeIsInvalid) {
    return null;
  }

  if (props.timeType === TimeType.Absolute) {
    return <AbsoluteTimeRangeInput {...props} />;
  }

  if (
    props.propertyOptions?.timestampGranularity === TimestampGranularity.Time
  ) {
    return <TimeOfDayRangeInput {...props} />;
  }

  return <RelativeTimeRangeInput {...props} />;
};

const getChronologicalFieldOrder = (
  value: Partial<AbsoluteTimeRangeValue>,
): [TimeRangeField, TimeRangeField] => {
  if (!value.before || !value.after) {
    return ["before", "after"];
  }
  const [first, second] = sortedAbsoluteRangeBounds({
    before: value.before,
    after: value.after,
  });
  return [first.field, second.field];
};

const AbsoluteTimeRangeInput = (props: AbsoluteTimeRangeInputProps) => {
  const { value, onUpdate, hideTime } = props;
  const beforeValue = value?.before as string | undefined;
  const afterValue = value?.after as string | undefined;

  // The bounds may be stored in either order, so display them chronologically
  // while each input keeps writing to its own field.
  // The order is computed once on mount and stays fixed while the editor is open
  const [[firstField, secondField]] = useState(() =>
    getChronologicalFieldOrder({ before: beforeValue, after: afterValue }),
  );

  const setAfterValue = (
    newValue: string,
    tsGranularity?: TimestampGranularity,
  ) => {
    onUpdate((condition) => {
      changeConditionValueToObject(condition);
      condition.value.after = newValue;
      if (tsGranularity) {
        setTimestampGranularityMutative(condition, tsGranularity);
      }
    });
  };
  const setBeforeValue = (
    newValue: string,
    tsGranularity?: TimestampGranularity,
  ) => {
    onUpdate((condition) => {
      changeConditionValueToObject(condition);
      condition.value.before = newValue;
      if (tsGranularity) {
        setTimestampGranularityMutative(condition, tsGranularity);
      }
    });
  };

  // Handles defaulting the 'before' value
  useEffect(() => {
    if (!beforeValue) {
      setBeforeValue(formatValue(newDate()));
    }
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  }, [beforeValue, setBeforeValue]);

  // Handles defaulting the 'after' value
  useEffect(() => {
    if (!afterValue) {
      const now = newDate();
      const oneMonthLater = newDate(now);
      oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);

      // Handles end of the month edge cases when the amount of days
      // in consecutive months are not aligned.
      while (now.getMonth() === oneMonthLater.getMonth()) {
        oneMonthLater.setDate(oneMonthLater.getDate() - 1);
      }

      setAfterValue(formatValue(oneMonthLater));
    }
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  }, [afterValue, setAfterValue]);

  if (!beforeValue || !afterValue) {
    return null;
  }

  const boundValues = { before: beforeValue, after: afterValue };
  const boundSetters = { before: setBeforeValue, after: setAfterValue };

  return (
    <>
      <AbsoluteTimestampInput
        isDisabled={props.isDisabled}
        hideTime={hideTime}
        onChange={boundSetters[firstField]}
        value={boundValues[firstField]}
      />
      <Row as={Text} flexShrink={0} mt={1.5}>
        and
      </Row>
      <AbsoluteTimestampInput
        isDisabled={props.isDisabled}
        hideTime={hideTime}
        onChange={boundSetters[secondField]}
        value={boundValues[secondField]}
      />
    </>
  );
};

const TimeOfDayRangeInput = (props: RelativeTimeRangeInputProps) => {
  const { value, onUpdate } = props;
  const beforeValue = value?.before as string | undefined;
  const afterValue = value?.after as string | undefined;

  const setAfterValue = (newValue: Date) => {
    onUpdate((condition) => {
      changeConditionValueToObject(condition);
      condition.value.after = formatValue(newValue);
    });
  };
  const setBeforeValue = (newValue: Date) => {
    onUpdate((condition) => {
      changeConditionValueToObject(condition);
      condition.value.before = formatValue(newValue);
    });
  };

  // Handles defaulting the 'before' value
  useEffect(() => {
    if (!beforeValue) {
      setBeforeValue(newDate());
    }
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  }, [beforeValue, setBeforeValue]);

  // Handles defaulting the 'after' value
  useEffect(() => {
    if (!afterValue) {
      setAfterValue(newDate());
    }
    // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
    // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
    // TODO with an explanation of why we don't want all deps listed.
    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  }, [afterValue, setAfterValue]);

  if (!beforeValue || !afterValue) {
    return null;
  }

  return (
    <>
      <TimeSelect
        value={newDate(beforeValue)}
        onChange={setBeforeValue}
        hideTimeZone
      />
      <Row as={Text} flexShrink={0} mt={1.5}>
        and
      </Row>
      <TimeSelect
        value={newDate(afterValue)}
        onChange={setAfterValue}
        hideTimeZone
      />
    </>
  );
};

const changeConditionValueToObject = (condition: any) => {
  if (!condition.value || typeof condition.value !== "object") {
    condition.value = {};
  }
};

const RelativeTimeRangeInput = (props: RelativeTimeRangeInputProps) => {
  const { value, onUpdate, propertyOptions, hideTime } = props;
  const beforeValue = value?.before as IntervalValue;
  const afterValue = value?.after as IntervalValue;
  const timestampGranularity = propertyOptions?.timestampGranularity;

  // `between` conditions still only have a single granularity shared between the two values.
  //  Since for non-full granularity, only granularity=interval is supported, intervals must also match.
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (
      beforeValue &&
      afterValue &&
      timestampGranularity &&
      timestampGranularity !== TimestampGranularity.Full &&
      beforeValue.interval !== afterValue.interval
    ) {
      onUpdate((condition) => {
        changeConditionValueToObject(condition);
        if (
          !condition.value.after ||
          typeof condition.value.after !== "object"
        ) {
          condition.value.after = {};
        }
        condition.value.after.interval = beforeValue.interval;
      });
    }
  }, [beforeValue, afterValue, timestampGranularity]);

  const setBeforeValue = (
    newValue: IntervalValue,
    tsGranularity?: TimestampGranularity,
  ) => {
    const newVal = { before: newValue, after: value?.after };
    if (tsGranularity && tsGranularity !== TimestampGranularity.Full) {
      newVal.after = { ...newVal.after, interval: newVal.before.interval };
    }
    onUpdate((condition) => {
      changeConditionValueToObject(condition);
      condition.value.before = newValue;
      if (tsGranularity) {
        setTimestampGranularityMutative(condition, tsGranularity);
      }
    });
  };

  const setAfterValue = (
    newValue: IntervalValue,
    tsGranularity?: TimestampGranularity,
  ) => {
    const newVal = { after: newValue, before: value?.before };
    if (tsGranularity && tsGranularity !== TimestampGranularity.Full) {
      newVal.before = { ...newVal.before, interval: newVal.after.interval };
    }
    onUpdate((condition) => {
      changeConditionValueToObject(condition);
      condition.value.after = newValue;
      if (tsGranularity) {
        setTimestampGranularityMutative(condition, tsGranularity);
      }
    });
  };

  return (
    <>
      <TimeIntervalInput
        error={props.error}
        isDisabled={props.isDisabled}
        hideTime={hideTime}
        onChange={setBeforeValue}
        value={beforeValue}
        timestampGranularity={timestampGranularity}
        removePortal={props.removePortal}
        maxRelativeWindowDays={props.maxRelativeWindowDays}
      />
      <Row as={Text} flexShrink={0} mt={1.5}>
        and
      </Row>
      <TimeIntervalInput
        isRangeEnd
        isDisabled={props.isDisabled}
        error={props.error}
        hideTime={hideTime}
        onChange={setAfterValue}
        value={afterValue}
        timestampGranularity={timestampGranularity}
        removePortal={props.removePortal}
        maxRelativeWindowDays={props.maxRelativeWindowDays}
      />
    </>
  );
};

const getIntervalOption = (
  interval: IntervalUnit,
  timestampGranularity = TimestampGranularity.Full,
) => {
  switch (timestampGranularity) {
    case TimestampGranularity.Day:
      if (interval !== IntervalUnit.Day) {
        console.warn("Time interval does not match granularity");
      }
      return CalendarIntervalUnit.Day;
    case TimestampGranularity.Month:
      if (interval !== IntervalUnit.Month) {
        console.warn("Time interval does not match granularity");
      }
      return CalendarIntervalUnit.Month;
    case TimestampGranularity.Year:
      if (interval !== IntervalUnit.Year) {
        console.warn("Time interval does not match granularity");
      }
      return CalendarIntervalUnit.Year;
    case TimestampGranularity.Full:
    default:
      return interval;
  }
};

const relativeValueNeedsInitialization = (value: any) => {
  return (
    (typeof value?.quantity !== "number" && !value?.interval) ||
    !value?.direction
  );
};

const TimeIntervalInput = ({
  error,
  isDisabled = false,
  value,
  timestampGranularity,
  onChange,
  isRangeEnd = false,
  showOnlyCalendarIntervals = false,
  hideTime = false,
  showRelativeDirections = true,
  removePortal,
  showCurrentOption = true,
  maxRelativeWindowDays,
}: {
  error?: ReactNode;
  value: IntervalValue;
  timestampGranularity?: TimestampGranularity;
  isDisabled?: boolean;
  onChange: (
    value: IntervalValue,
    timestampGranularity?: TimestampGranularity,
  ) => void;
  isRangeEnd?: boolean;
  hideTime: boolean;
  showOnlyCalendarIntervals?: boolean;
  showRelativeDirections?: boolean;
  showCurrentOption?: boolean;
  removePortal?: boolean;
  maxRelativeWindowDays?: number;
}) => {
  // Handles default values.
  const needsInitialization = relativeValueNeedsInitialization(value);
  useEffect(() => {
    if (needsInitialization) {
      onChange({
        quantity: value?.quantity ?? (isRangeEnd ? 2 : 1),
        interval: value?.interval ?? defaultRelativeValue.interval,
        direction: value?.direction ?? defaultRelativeValue.direction,
      });
    }
    // No dependency array is necessary, this needs to check on every render due to race conditions with other value initializers
  });

  const isCurrentCalendarInterval = showCurrentOption
    ? getIsCurrentTimeType(value, timestampGranularity)
    : false;

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (
      showOnlyCalendarIntervals &&
      (!timestampGranularity ||
        timestampGranularity === TimestampGranularity.Full)
    ) {
      onChange(
        { ...value, interval: IntervalUnit.Month },
        TimestampGranularity.Month,
      );
    }
  }, [showOnlyCalendarIntervals, timestampGranularity]);

  if (needsInitialization) {
    return null;
  }

  const lockInterval =
    isRangeEnd &&
    timestampGranularity &&
    timestampGranularity !== TimestampGranularity.Full;

  // Cap-aware inputs: units that can't fit a single quantity within the cap
  // are hidden, and the quantity input clamps to the selected unit's max.
  // Saved values beyond the cap render as-is: their unit stays selectable,
  // and a max of 0 disables clamping so an edit can't silently turn the
  // value into "current" (quantity 0).
  const currentInterval = getIntervalOption(
    value.interval,
    timestampGranularity,
  );
  const maxQuantity = getMaxWindowQuantity(
    currentInterval,
    maxRelativeWindowDays,
  );
  const clampQuantity = (quantity: number | undefined, max = maxQuantity) =>
    quantity != null && max ? Math.min(quantity, max) : quantity;
  const intervalOptions = (
    isCurrentCalendarInterval
      ? CalendarIntervalOptionsSingular
      : showOnlyCalendarIntervals
        ? CalendarIntervalOptions
        : hideTime
          ? DateIntervalOptions
          : IntervalOptions
  ).filter(
    (option) =>
      option.value === currentInterval ||
      getMaxWindowQuantity(option.value, maxRelativeWindowDays) !== 0,
  );

  return (
    <>
      {showRelativeDirections && (
        <Select
          removePortal={removePortal}
          isDisabled={isDisabled}
          placeholder="Select an option..."
          options={[
            { label: "previous", value: RelativeDirection.Backward },
            { label: "next", value: RelativeDirection.Forward },
            ...(showCurrentOption
              ? [{ label: "current", value: "current" }]
              : []),
          ]}
          width="auto"
          value={isCurrentCalendarInterval ? "current" : value.direction}
          onChange={(direction: RelativeDirection | Current | undefined) => {
            if (direction === undefined) {
              return;
            }

            if (direction === "current") {
              const newInterval = onlyKeepCalendarIntervalUnits(
                value?.interval,
              );
              onChange(
                {
                  quantity: 0,
                  direction: RelativeDirection.Backward,
                  interval: newInterval,
                },
                getMatchingGranularity(newInterval),
              );
            } else {
              const newValue = { ...value, direction };

              if (isCurrentCalendarInterval) {
                // If swapping away from "current", need to change the quantity away from 0 to avoid getting stuck in "current"
                newValue.quantity = 1;
              }
              onChange(newValue);
            }
          }}
        />
      )}
      {!isCurrentCalendarInterval && (
        <Column width="48px" flex="0 0 auto">
          <HiddenFormLabel label="Quantity">
            <NumberInput
              isInvalid={Boolean(error)}
              isDisabled={isDisabled}
              placeholder="# of"
              max={maxQuantity || undefined}
              value={value?.quantity != null ? value.quantity : undefined}
              onChange={(num) =>
                onChange({ ...value, quantity: clampQuantity(num) })
              }
            />
          </HiddenFormLabel>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Column>
      )}
      <Select
        removePortal={removePortal}
        isDisabled={isDisabled || lockInterval}
        options={intervalOptions}
        placeholder="Unit..."
        width="auto"
        value={getIntervalOption(value.interval, timestampGranularity)}
        onChange={(interval) => {
          if (interval === undefined) {
            return;
          }

          const quantity = clampQuantity(
            value.quantity,
            getMaxWindowQuantity(interval, maxRelativeWindowDays),
          );

          if (isCalendarInterval(interval)) {
            let newInterval: IntervalUnit;
            let granularity: TimestampGranularity;
            if (interval === CalendarIntervalUnit.Day) {
              newInterval = IntervalUnit.Day;
              granularity = TimestampGranularity.Day;
            } else if (interval === CalendarIntervalUnit.Month) {
              newInterval = IntervalUnit.Month;
              granularity = TimestampGranularity.Month;
            } else if (interval === CalendarIntervalUnit.Year) {
              newInterval = IntervalUnit.Year;
              granularity = TimestampGranularity.Year;
            } else {
              throw new Error("Unrecognized calendar interval");
            }
            onChange(
              { ...value, quantity, interval: newInterval },
              granularity,
            );
          } else {
            onChange(
              { ...value, quantity, interval },
              TimestampGranularity.Full,
            );
          }
        }}
      />
    </>
  );
};

const AbsoluteTimestampInput = ({
  value,
  onChange,
  hideTime = false,
  isDisabled = false,
}: {
  value: string | null;
  isDisabled?: boolean;
  onChange: (value: string) => void;
  hideTime: boolean;
}) => {
  // Handle defaulting the value
  useEffect(() => {
    if (!value) {
      onChange(formatValue(newDate()));
    }
  }, [value, onChange]);

  if (!value) {
    return null;
  }

  return (
    <DateTimeSelect
      isDisabled={isDisabled}
      hideTime={hideTime}
      value={newDate(value)}
      onChange={(value) => {
        onChange(formatValue(value));
      }}
    />
  );
};

export function formatValue(value: Date) {
  // Make sure seconds are always at 00
  return moment(value).utc(true).format("YYYY-MM-DD HH:mm:00");
}

export const RealtimeEventWindowInput = ({
  window,
  onUpdate,
}: {
  window: Window;
  onUpdate: (updater: UpdaterEnforceDefined<EventCondition>) => void;
}) => {
  return (
    <Row gap={2}>
      <HiddenFormLabel label="Window quantity">
        <NumberInput
          min={0}
          onChange={(quantity) => {
            onUpdate((condition) => {
              if (
                quantity != undefined &&
                condition.window &&
                isIntervalType(condition.window.value)
              ) {
                condition.window.value.quantity = quantity;
              }
            });
          }}
          value={
            isIntervalType(window.value) ? window.value.quantity : undefined
          }
          width="auto"
        />
      </HiddenFormLabel>
      <Select
        options={realtimeEventWindowIntervalOptions}
        onChange={(intervalUnit) => {
          onUpdate((condition) => {
            if (
              intervalUnit &&
              condition.window &&
              isIntervalType(condition.window.value)
            ) {
              condition.window.value.interval = intervalUnit as IntervalUnit;
            }
          });
        }}
        width="auto"
        value={isIntervalType(window.value) ? window.value.interval : undefined}
      />
    </Row>
  );
};

const realtimeEventWindowIntervalOptions = [
  { value: IntervalUnit.Minute, label: "Minute(s)" },
  { value: IntervalUnit.Hour, label: "Hour(s)" },
];
